import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Card,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  FormControl,
  Autocomplete,
  TextField,
  Stack,
} from "@mui/material";

import Loader from "../../../Loader/loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Checkbox from "@mui/material/Checkbox";
import { useNavigate } from "react-router-dom";
import MDTypography from "components/MDTypography";
import { SetBlastIVR, getOutboundCdrRequest, getCallerIDRequest } from "../../../api/api";

function IVRBlastForm({ onSuccess }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trunkOption, settrunkOption] = React.useState([]);
  const [callerIdOptions, setCallerIdOptions] = useState([]);
  const validationSchema = Yup.object({
    trunk_name: Yup.string().required("Trunk is required"),
    number: Yup.string().required("Number is required"),
    caller_id: Yup.array()
      .min(1, "At least one Caller ID is required")
      .required("Caller ID is required"),

    count: Yup.string().required("Number of Calls is required"),
    max_cc: Yup.number()
      .typeError("Max CC must be a number")
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .moreThan(0, "Max CC must be greater than 0"),
  });

  const formik = useFormik({
    initialValues: {
      trunk_name: "",
      number: "",
      caller_id: [],
      count: "",
      audio_file: "",
      max_cc: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);
        const response = await SetBlastIVR({
          trunk_name: values.trunk_name,
          number: values.number,
          caller_id: values.caller_id.join(","),
          count: values.count,
          audio_file: values.audio_file,
          max_cc: values.max_cc,
        });

        // agar API error throw nahi karti, matlab success hai
        resetForm();
        onSuccess?.();
      } catch (error) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    const fetchtrunkOption = async () => {
      try {
        const res = await getOutboundCdrRequest();
        settrunkOption(res.data.data); // Make sure your API returns `data.data`
      } catch (error) {
        console.error("Error fetching trunk_id options:", error);
      }
    };

    fetchtrunkOption();
  }, []);

  React.useEffect(() => {
    const fetchCallerIds = async () => {
      try {
        const res = await getCallerIDRequest();

        setCallerIdOptions(
          res?.data?.data?.map((item) => ({
            label: item.caller_id,
            value: item.caller_id,
          })) || []
        );
      } catch (error) {
        console.error("Error fetching caller ids:", error);
      }
    };

    fetchCallerIds();
  }, []);

  return (
    <Grid item xs={12} md={6} lg={5}>
      <Card sx={{ padding: 3 }}>
        <MDTypography variant="h5" fontWeight="bold" mb={2}>
          IVR Blast
        </MDTypography>

        {isSubmitting ? (
          <Grid container justifyContent="center" alignItems="center" sx={{ py: 5 }}>
            <Loader />
          </Grid>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="trunk_id">Trunk *</InputLabel>
                  <FormControl
                    fullWidth
                    error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
                  >
                    <Autocomplete
                      options={trunkOption}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        trunkOption.find((opt) => opt.name === formik.values.trunk_name) || null
                      }
                      onChange={(_, newValue) => {
                        formik.setFieldValue("trunk_name", newValue ? newValue.name : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Trunk"
                          error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
                          helperText={formik.touched.trunk_id && formik.errors.trunk_id}
                        />
                      )}
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="number">Number *</InputLabel>
                  <OutlinedInput
                    type="number"
                    id="number"
                    name="number"
                    fullWidth
                    placeholder="Enter Number "
                    value={formik.values.number}
                    onChange={formik.handleChange}
                    error={formik.touched.number && Boolean(formik.errors.number)}
                  />
                  {formik.touched.number && formik.errors.number && (
                    <FormHelperText error>{formik.errors.number}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="caller_id">Caller ID *</InputLabel>

                  <Autocomplete
                    multiple
                    options={callerIdOptions}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.label}
                    value={callerIdOptions.filter((option) =>
                      formik.values.caller_id.includes(option.value)
                    )}
                    onChange={(_, newValue) => {
                      formik.setFieldValue(
                        "caller_id",
                        newValue.map((item) => item.value)
                      );
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Caller IDs"
                        error={formik.touched.caller_id && Boolean(formik.errors.caller_id)}
                        helperText={formik.touched.caller_id && formik.errors.caller_id}
                      />
                    )}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="count">Number of Calls *</InputLabel>
                  <OutlinedInput
                    type="text"
                    id="count"
                    name="count"
                    fullWidth
                    placeholder="Enter Number of Calls"
                    value={formik.values.count}
                    onChange={formik.handleChange}
                    error={formik.touched.count && Boolean(formik.errors.count)}
                  />
                  {formik.touched.count && formik.errors.count && (
                    <FormHelperText error>{formik.errors.count}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel sx={{ minWidth: 120 }}>Audio File</InputLabel>
                  <OutlinedInput
                    name="audio_file"
                    type="file"
                    fullWidth
                    inputProps={{ accept: "audio/*" }}
                    onChange={(event) =>
                      formik.setFieldValue("audio_file", event.currentTarget.files[0])
                    }
                    error={formik.touched.audio_file && Boolean(formik.errors.audio_file)}
                  />
                </Stack>
                {formik.errors.audio_file && (
                  <FormHelperText error sx={{ marginLeft: "150px" }}>
                    {formik.errors.audio_file}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="max_cc">Max CC </InputLabel>
                  <OutlinedInput
                    type="text"
                    id="max_cc"
                    name="max_cc"
                    fullWidth
                    placeholder="Enter Max CC"
                    value={formik.values.max_cc}
                    onChange={formik.handleChange}
                    error={formik.touched.max_cc && Boolean(formik.errors.max_cc)}
                  />
                  {formik.touched.max_cc && formik.errors.max_cc && (
                    <FormHelperText error>{formik.errors.max_cc}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#1A73E8",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#1669C1",
                    },
                  }}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Card>
    </Grid>
  );
}
IVRBlastForm.propTypes = {
  onSuccess: PropTypes.func,
};
export default IVRBlastForm;
